import { QuoteAnalyticsEvents } from '@cowbell/data/analytics';
import { useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import type { CoverageOption } from '../../../../../api/submissions/submission-coverages.types';
import { useQueryParams } from '../../../../../components/providers/QueryParamsProvider';
import { buildIndication } from '../../shared/quote-options/quoteOptionIndication';
import { toOptionTerms } from '../../shared/quote-options/quoteOptionsModel';
import { useSaveQuoteOption } from '../../shared/quote-options/useSaveQuoteOption';
import useNotifications from '../../../../../_lib/notifications/useNotifications';
import { updateQuoteCartList } from '../../../../../api';
import type { UpdateQuoteCartListPayload } from '../../../../../api/quotes.api.types';
import { useActor } from '../../../../../components/hooks/useActor';
import { useSupportedProducts } from '../../../../../components/hooks/useSupportedProducts';
import { useAnalytics } from '../../../../../config/analytics';
import { manageAPIError } from '../../../../../utils';
import { useGetQuoteCartList } from '../../../../agencies/quotes/PrimeX/hooks/useGetQuoteCartList';
import {
  firstRatedLineName,
  submissionProductCountry,
} from '../../../../submissions/modules/coverages/constants';
import { PRODUCT_SCHEMAS } from '../../../../submissions/modules/product-selector/productSchemas';
import type { RaterXPreset } from '../api/types/rater-x-preset';
import { formatCoveragesForState } from '../transformers/coverages';
import { formatRatingOptions } from '../transformers/uw/ratingOptions';
import {
  buildOptionParts,
  type TermsForParts,
} from '../utils/quoteOptionParts';
import type { PrimeXQuoteHandlers } from '../types';
import { basePayloadBuilder, reduceFieldValues } from '../utils/api';
import {
  buildCoverageOptionMap,
  generateFieldValues,
} from '../utils/rater-x/helpers';
import {
  type CustomizeQuoteState,
  isCustomizeQuoteState,
} from '../views/customize-quote/types';
import type { PrimeXSimpleQuoteState } from '../views/simple-request-quote/types';

const useAddQuoteToCart = <T extends QuoteState>(
  quoteState: T,
  setCartCount: PrimeXQuoteHandlers['setCartCount'],
  totalPremium: number | undefined = undefined,
  refers: boolean | undefined = false
) => {
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const actor = useActor();
  const { submissionId } = useParams<{ submissionId?: string }>();

  const { query } = useQueryParams();
  const supportedQuery = useSupportedProducts(
    submissionProductCountry(quoteState.submission)
  );

  // A submission saves coverage options. Without one the flow is account-keyed and still
  // adds to the cart.
  const cartQuery = useGetQuoteCartList({
    accountData: quoteState.account,
    accountId: quoteState.account.id,
    productType: quoteState.productType,
    enabled: !submissionId,
  });
  const saveOption = useSaveQuoteOption({
    submissionId: submissionId ?? '',
    seedOptionId: query?.optionId || undefined,
  });

  const track = (quoteId: string, premiumAmount: number | undefined) =>
    analytics.track(QuoteAnalyticsEvents.ADDED_TO_CART, {
      accountId: quoteState.account.id ?? '',
      agencyId: quoteState.agencyId ?? '',
      agencyName: quoteState.agencyName ?? '',
      agentId: actor.id ?? '',
      productType: quoteState.productType ?? '',
      lineOfBusiness:
        PRODUCT_SCHEMAS[quoteState.productType]?.lineOfBusiness ?? '',
      quoteStatus: quoteState.submission?.submissionStatus ?? '',
      submissionId: quoteState.submission?.id,
      coverageType: quoteState.productType ?? '',
      quoteId,
      ...(premiumAmount != null && {
        quotePremiumAmount: String(premiumAmount),
      }),
    });

  const addToOptions = async (
    payload: ReturnType<typeof buildAddToCartPayload>,
    premiumAmount: number | undefined,
    isReferred: boolean
  ): Promise<CoverageOption> => {
    const terms = toOptionTerms(payload) as TermsForParts;
    try {
      const saved = await saveOption({
        product: quoteState.productType,
        terms,
        indication: buildIndication(premiumAmount, isReferred),
        parts: buildOptionParts(terms, {
          productType: quoteState.productType,
          singlePartName:
            quoteState.coverageOption?.coverages[0]?.part.value ??
            firstRatedLineName(
              quoteState.productType,
              quoteState.submission?.productLines,
              supportedQuery.data
            ),
        }),
      });
      notifications.show({ title: 'Coverage option saved.', type: 'success' });
      // The option id stands in for a quote id: no quote exists until the option is quoted.
      track(saved.id, premiumAmount);
      return saved;
    } catch (error) {
      notifications.show({
        title: 'Unable to save coverage option',
        type: 'error',
        body: manageAPIError(error as AxiosError, null),
      });
      /* throws error back to call-site to re-enable add to cart button */
      throw new Error();
    }
  };

  return async (quoteOption: RaterXPreset | undefined = undefined) => {
    const payload = buildAddToCartPayload(
      quoteState,
      quoteOption,
      totalPremium,
      refers
    );
    const premiumAmount = totalPremium ?? quoteOption?.totalPremium;

    if (submissionId) {
      await addToOptions(
        payload,
        premiumAmount,
        refers || (quoteOption?.refers ?? false)
      );
      return;
    }

    setCartCount(quoteState.cartCount + 1);
    notifications.show({
      title: 'Quote added to cart.',
      type: 'success',
    });

    return updateQuoteCartList(quoteState.productType, quoteState.account.id, {
      data: payload as UpdateQuoteCartListPayload,
      params: { agencyId: quoteState.agencyId },
    })
      .then((response) => {
        track(response.data.quoteId, premiumAmount);

        setTimeout(() => {
          queryClient.invalidateQueries([
            'request-quote-cart',
            quoteState.account?.id,
            quoteState.productType,
          ]);
        }, 500);
      })
      .catch((error) => {
        notifications.show({
          title: 'Unable to add quote to cart',
          type: 'error',
          body: manageAPIError(error, null),
        });
        setCartCount(cartQuery.data?.count ?? 0);
        /* throws error back to call-site to re-enable add to cart button */
        throw new Error();
      });
  };
};

export default useAddQuoteToCart;

/** The preset name the cart keeps on a customized quote. */
export const CUSTOM_PRESET_NAME = 'Custom';

/** The cart's request for an option, or for a customized quote: the agency's quote flows and the broker experience's. */
export function buildAddToCartPayload(
  quoteState: QuoteState,
  quoteOption: RaterXPreset | undefined,
  totalPremium: number | undefined,
  refers: boolean
) {
  if (isCustomizeQuoteState(quoteState)) {
    const basePayload = basePayloadBuilder(quoteState);

    return {
      presets: CUSTOM_PRESET_NAME,
      ...(!refers && { totalPremium }),
      ...basePayload,
    };
  }

  if (!quoteOption) {
    throw new Error('Missing quote option on add to cart.');
  }

  const coverageOptionMap = buildCoverageOptionMap(quoteState.coverageOption);

  return {
    effectiveDate: quoteState.effectiveDate,
    endDate: quoteState.endDate,
    ...(quoteState.quoteCommissionRate != null && {
      commissionRate: quoteState.quoteCommissionRate,
    }),
    isRenewal: quoteState.isRenewal,
    presets: quoteOption.presetName,
    carrierPrefix: quoteOption.carrierPrefix,
    treatyId: quoteOption.treatyId,
    fieldValues: quoteState.fieldValues,
    ...(!quoteOption.refers && { totalPremium: quoteOption.totalPremium }),
    ...(quoteOption.components && !_.isEmpty(quoteOption.components)
      ? {
          policyValue: quoteOption.global.value,
          components: buildComponentDynamicFields(
            quoteOption.components,
            coverageOptionMap
          ),
        }
      : {
          aggregateLimit: Number(quoteOption.global.value.props?.limit?.value),
          deductible: Number(quoteOption.global.value.props?.deductible?.value),

          retroactivePeriod:
            quoteOption.global.value.props?.retroactive?.props?.period?.value,
          waitingPeriod: Number(
            quoteOption.global.value.props?.waitingPeriod?.value
          ),
          fieldValues: buildSimpleQuoteDynamicFields(
            quoteState.fieldValues,
            quoteOption?.coverageCategories,
            quoteOption.rateModifierCategories
          ),
        }),
  };
}

export function buildComponentDynamicFields(
  components: RaterXPreset['components'],
  coverageOptionMap: Record<string, { limit: number; deductible?: number }>
) {
  if (!components) {
    return {};
  }

  return Object.entries(components).reduce((acc, [key, component]) => {
    const coverages = formatCoveragesForState(component.coverageCategories);
    const ratingOptions = formatRatingOptions(component.rateModifierCategories);
    let policyValue = component.global.value;

    if (coverageOptionMap[key]) {
      policyValue = {
        ...policyValue,
        props: {
          ...policyValue.props,
          limit: { value: coverageOptionMap[key].limit },
          ...(coverageOptionMap[key].deductible && {
            deductible: { value: coverageOptionMap[key].deductible },
          }),
        },
      };
    }

    const obj = generateFieldValues([], coverages, ratingOptions).reduce(
      reduceFieldValues,
      {}
    );

    return {
      ...acc,
      [key]: {
        policyValue,
        fieldValues: Object.entries(obj).map(([key, value]) => ({
          fieldId: key,
          value,
        })),
      },
    };
  }, {});
}

function buildSimpleQuoteDynamicFields(
  fieldValues: PrimeXSimpleQuoteState['fieldValues'],
  coverageCategories: RaterXPreset['coverageCategories'] | undefined,
  rateModifierCategories: RaterXPreset['rateModifierCategories'] | undefined
) {
  if (!coverageCategories || !rateModifierCategories) {
    throw new Error('Missing coverage or rate modifier categories');
  }

  const coverages = formatCoveragesForState(coverageCategories);
  const ratingOptions = formatRatingOptions(rateModifierCategories);

  const obj = generateFieldValues(fieldValues, coverages, ratingOptions).reduce(
    reduceFieldValues,
    {}
  );

  return Object.entries(obj).map(([key, value]) => ({
    fieldId: key,
    value,
  }));
}

export type QuoteState = PrimeXSimpleQuoteState | CustomizeQuoteState;
